/**
 * Studio API client.
 *
 * REST + SSE for the studio workflow. Auth headers are injected from the
 * Supabase session (mirror of `lib/api.ts` pattern). The streaming function
 * follows the same SSE protocol as streamBrainstorm/streamZinsser: lines
 * separated by "\n\n", each prefixed with "data: ", terminated by "[DONE]".
 */

import { supabase } from './supabase'

const API_BASE = '/api'

async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

async function authHeaders(json: boolean = false): Promise<HeadersInit> {
  const token = await getAccessToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

async function request<T>(
  endpoint: string,
  init?: RequestInit,
): Promise<T> {
  const isJson = init?.body && typeof init.body === 'string'
  const headers = {
    ...(await authHeaders(Boolean(isJson))),
    ...(init?.headers || {}),
  }
  const resp = await fetch(`${API_BASE}${endpoint}`, { ...init, headers })
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}: ${text}`)
  }
  // 204 No Content has no body
  if (resp.status === 204) return undefined as T
  return resp.json() as Promise<T>
}


// ═══════════════════════════════════════════════════════════════════════════
// Types — mirror app/api/studio_entities.py response shapes
// ═══════════════════════════════════════════════════════════════════════════

export type StudioBlockType = 'governance' | 'operations' | 'development' | 'distribution'
export type StudioBlockStatus =
  | 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'overdue' | 'missing'
export type StudioCardKind = 'note' | 'image' | 'pdf' | 'docx' | 'link' | 'email'
export type StudioSectionKind =
  | 'intent' | 'reference' | 'outcome' | 'next' | 'scaffold' | 'custom'

export interface StudioUniverse {
  id: string
  name: string
  slug: string
  color: string | null
  position: number
  display_config: Record<string, unknown> | null
  archived_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface StudioProject {
  id: string
  universe_id: string
  name: string
  slug: string
  position: number
  display_config: Record<string, unknown> | null
  agent_notes: string | null
  user_notes: string | null
  archived_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface StudioBlock {
  id: string
  universe_id: string
  project_id: string
  title: string
  body_md: string
  output_intent: string | null
  block_type: StudioBlockType
  starts_at: string | null
  ends_at: string | null
  all_day: boolean
  duration_minutes: number | null
  status: StudioBlockStatus
  status_locked: boolean
  score: number | null
  score_due: boolean
  intent_locked: boolean
  outcome_captured: boolean
  fast: boolean
  meta: Record<string, unknown> | null
  archived_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface StudioBlockSection {
  id: string
  block_id: string
  kind: StudioSectionKind
  content_md: string
  updated_at: string | null
}

export interface StudioCard {
  id: string
  block_id: string | null
  project_id: string | null
  kind: StudioCardKind
  title: string | null
  content_md: string | null
  file_url: string | null
  external_url: string | null
  position: number
  meta: Record<string, unknown> | null
  archived_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface StudioSessionSummary {
  session_id: string
  name: string | null
  status: string
  total_turns: number
  last_block_id: string | null
  last_project_id: string | null
  last_universe_id: string | null
  created_at: string | null
  updated_at: string | null
  last_message_at: string | null
}

export interface StudioSessionTurn {
  turn_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: string
  thinking?: string | null
  node_outputs?: Record<string, unknown> | null
  timestamp: string | null
}

export interface StudioSessionDetail extends StudioSessionSummary {
  session_context: Record<string, unknown> | null
  turns: StudioSessionTurn[]
}


// ═══════════════════════════════════════════════════════════════════════════
// Universes
// ═══════════════════════════════════════════════════════════════════════════

export const listUniverses = (includeArchived = false) =>
  request<StudioUniverse[]>(
    `/studio/universes?include_archived=${includeArchived}`,
  )

export const createUniverse = (body: { name: string; color?: string; slug?: string; position?: number }) =>
  request<StudioUniverse>('/studio/universes', {
    method: 'POST',
    body: JSON.stringify(body),
  })

export const updateUniverse = (id: string, body: Partial<{ name: string; color: string; position: number; display_config: Record<string, unknown> }>) =>
  request<StudioUniverse>(`/studio/universes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })

export const archiveUniverse = (id: string) =>
  request<StudioUniverse>(`/studio/universes/${id}/archive`, { method: 'POST' })

export const restoreUniverse = (id: string) =>
  request<StudioUniverse>(`/studio/universes/${id}/restore`, { method: 'POST' })


// ═══════════════════════════════════════════════════════════════════════════
// Projects
// ═══════════════════════════════════════════════════════════════════════════

export const listProjects = (params?: { universe_id?: string; include_archived?: boolean }) => {
  const qs = new URLSearchParams()
  if (params?.universe_id) qs.set('universe_id', params.universe_id)
  if (params?.include_archived) qs.set('include_archived', 'true')
  return request<StudioProject[]>(`/studio/projects?${qs}`)
}

export const createProject = (body: { universe_id: string; name: string; slug?: string; position?: number }) =>
  request<StudioProject>('/studio/projects', {
    method: 'POST',
    body: JSON.stringify(body),
  })

export const updateProject = (id: string, body: Partial<{ name: string; position: number; agent_notes: string; user_notes: string; display_config: Record<string, unknown> }>) =>
  request<StudioProject>(`/studio/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })

export const archiveProject = (id: string) =>
  request<StudioProject>(`/studio/projects/${id}/archive`, { method: 'POST' })


// ═══════════════════════════════════════════════════════════════════════════
// Blocks
// ═══════════════════════════════════════════════════════════════════════════

export interface ListBlocksParams {
  project_id?: string
  universe_id?: string
  status?: StudioBlockStatus
  block_type?: StudioBlockType
  starts_after?: string  // ISO 8601
  starts_before?: string
  score?: 0 | 1
  score_due?: boolean
  include_archived?: boolean
  order_by?: 'starts_at' | 'created_at' | 'title'
  order_dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export const listBlocks = (params?: ListBlocksParams) => {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  return request<StudioBlock[]>(`/studio/blocks?${qs}`)
}

export const getBlock = (id: string) =>
  request<StudioBlock>(`/studio/blocks/${id}`)

export interface CreateBlockBody {
  project_id: string
  title: string
  block_type: StudioBlockType
  output_intent?: string | null
  starts_at?: string | null
  ends_at?: string | null
  all_day?: boolean
  duration_minutes?: number | null
  body_md?: string
  fast?: boolean
  meta?: Record<string, unknown>
}

export const createBlock = (body: CreateBlockBody) =>
  request<StudioBlock>('/studio/blocks', {
    method: 'POST',
    body: JSON.stringify(body),
  })

export const updateBlock = (
  id: string,
  body: Partial<Omit<CreateBlockBody, 'project_id'>> & Partial<{
    status: StudioBlockStatus
    score: 0 | 1
    intent_locked: boolean
    outcome_captured: boolean
    status_locked: boolean
  }>,
) =>
  request<StudioBlock>(`/studio/blocks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })

export const archiveBlock = (id: string) =>
  request<StudioBlock>(`/studio/blocks/${id}/archive`, { method: 'POST' })

export const restoreBlock = (id: string) =>
  request<StudioBlock>(`/studio/blocks/${id}/restore`, { method: 'POST' })

export const listBlockSections = (id: string) =>
  request<StudioBlockSection[]>(`/studio/blocks/${id}/sections`)

export const upsertBlockSection = (id: string, body: { kind: StudioSectionKind; content_md: string }) =>
  request<StudioBlockSection>(`/studio/blocks/${id}/sections`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })

export const listBlockCards = (id: string) =>
  request<StudioCard[]>(`/studio/blocks/${id}/cards`)


// ═══════════════════════════════════════════════════════════════════════════
// Cards
// ═══════════════════════════════════════════════════════════════════════════

export const listCards = (params?: { block_id?: string; project_id?: string; kind?: StudioCardKind; include_archived?: boolean }) => {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  return request<StudioCard[]>(`/studio/cards?${qs}`)
}

export const createCard = (body: { kind: StudioCardKind; block_id?: string; project_id?: string; title?: string; content_md?: string; file_url?: string; external_url?: string; position?: number }) =>
  request<StudioCard>('/studio/cards', {
    method: 'POST',
    body: JSON.stringify(body),
  })


// ═══════════════════════════════════════════════════════════════════════════
// Sessions
// ═══════════════════════════════════════════════════════════════════════════

export const listSessions = (status: 'active' | 'archived' = 'active', limit = 50) =>
  request<StudioSessionSummary[]>(`/studio/sessions?status=${status}&limit=${limit}`)

export const getSession = (sessionId: string, turnLimit = 50) =>
  request<StudioSessionDetail>(`/studio/sessions/${sessionId}?turn_limit=${turnLimit}`)

export const createSession = (name?: string) =>
  request<StudioSessionSummary>('/studio/sessions', {
    method: 'POST',
    body: JSON.stringify({ name: name || null }),
  })

export const deleteSession = (sessionId: string) =>
  request<{ deleted: boolean }>(`/studio/sessions/${sessionId}`, { method: 'DELETE' })


// ═══════════════════════════════════════════════════════════════════════════
// Streaming chat — POST /events/studio/stream (SSE)
// ═══════════════════════════════════════════════════════════════════════════

export interface StudioStreamParams {
  content: string
  session_id?: string
  block_id?: string | null
  project_id?: string | null
  universe_id?: string | null
  run_mode?: 'fast' | 'deep'
}

export interface StudioToolCall {
  name: string
  input: Record<string, unknown>
}

export interface StudioToolResult {
  name: string
  ok: boolean
  summary: string
}

export interface StudioStreamCallbacks {
  onThinkingStart?: () => void
  onThinkingDelta?: (delta: string) => void
  onThinkingComplete?: () => void
  onToolCall?: (call: StudioToolCall) => void
  onToolResult?: (result: StudioToolResult) => void
  onSessionComplete?: (payload: { session_id: string; response: string; saved: boolean }) => void
  onDone: () => void
  onError: (error: string) => void
}

/**
 * Open an SSE stream for a Studio chat turn.
 *
 * Server emits: thinking_start / thinking / thinking_complete /
 * studio_tool_call / studio_tool_result / session_complete / [DONE].
 *
 * The final response text comes through `onSessionComplete`. We don't
 * stream tokens for the response itself in this v1 because the triage
 * node doesn't emit per-token deltas yet — the model writes its reply
 * in the final assistant content block, which we get whole on the
 * session_complete event. Add token streaming later if it matters.
 */
export async function streamStudio(
  params: StudioStreamParams,
  callbacks: StudioStreamCallbacks,
): Promise<void> {
  const headers = {
    ...(await authHeaders(true)),
  }

  let resp: Response
  try {
    resp = await fetch(`${API_BASE}/events/studio/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    })
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : 'network error')
    return
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    callbacks.onError(text || `HTTP ${resp.status}`)
    return
  }

  const reader = resp.body?.getReader()
  if (!reader) {
    callbacks.onError('no response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') {
          callbacks.onDone()
          return
        }

        let event: { type?: string; [k: string]: unknown }
        try {
          event = JSON.parse(data)
        } catch (e) {
          console.warn('[studio] failed to parse SSE event:', e, data)
          continue
        }

        switch (event.type) {
          case 'thinking_start':
            callbacks.onThinkingStart?.()
            break
          case 'thinking':
            callbacks.onThinkingDelta?.(String(event.thinking || ''))
            break
          case 'thinking_complete':
            callbacks.onThinkingComplete?.()
            break
          case 'studio_tool_call':
            callbacks.onToolCall?.({
              name: String(event.name || ''),
              input: (event.input as Record<string, unknown>) || {},
            })
            break
          case 'studio_tool_result':
            callbacks.onToolResult?.({
              name: String(event.name || ''),
              ok: Boolean(event.ok),
              summary: String(event.summary || ''),
            })
            break
          case 'session_complete':
            callbacks.onSessionComplete?.({
              session_id: String(event.session_id || ''),
              response: String(event.response || ''),
              saved: Boolean(event.saved),
            })
            break
          case 'session_error':
          case 'error':
            callbacks.onError(String(event.error || 'unknown error'))
            return
          // 'triage_done' and 'done' are advisory; ignore.
        }
      }
    }
    callbacks.onDone()
  } finally {
    reader.releaseLock()
  }
}
